import { FilterPanelOperations } from '@aidkitorg/component-library';
import { FieldOption, FilterConfig } from '@aidkitorg/types/lib/admin-panel/filters';
import LZString from 'lz-string';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

export type DashboardFilterContextType = {
  // Current filter state
  filterConfig: FilterConfig | undefined;
  setFilterConfig: (config: FilterConfig | undefined) => void;
  
  // Filter panel ref and operations
  filterRef: React.MutableRefObject<FilterPanelOperations | null>;
  
  // Available fields for filtering
  availableFields: FieldOption[];
  setAvailableFields: (fields: FieldOption[]) => void;
  
  // Reset filters
  resetFilters: () => void;

  // enable URL filters. if enabled, the filter config is encoded as a URL query param and the filter panel will load initial view filters from it
  enableUrlFilters?: boolean;
};

const DashboardFilterContext = createContext<DashboardFilterContextType | undefined>(undefined);

export const useDashboardFilter = () => {
  const context = useContext(DashboardFilterContext);
  if (!context) {
    throw new Error('useDashboardFilter must be used within a DashboardFilterProvider');
  }
  return context;
};

type DashboardFilterProviderProps = {
  children: React.ReactNode;
  enableUrlFilters?: boolean;
  initialFilterConfig?: FilterConfig;
  initialAvailableFields?: FieldOption[];
  onFilterChange?: (config: FilterConfig | undefined) => void;
};

const URL_FILTER_PARAM = 'filters';

export const DashboardFilterProvider: React.FC<DashboardFilterProviderProps> = ({
  children,
  enableUrlFilters = false,
  initialFilterConfig,
  initialAvailableFields = [],
  onFilterChange
}) => {
  const filterRef = useRef<FilterPanelOperations | null>(null);
  const previousFilterConfigRef = useRef<string>('');
  
  // Initialize filter config from URL if enabled, otherwise use initial config
  const [filterConfig, setFilterConfigState] = useState<FilterConfig | undefined>(() => {
    if (enableUrlFilters) {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const encodedFilters = urlParams.get(URL_FILTER_PARAM);
        if (encodedFilters) {
          const decompressed = LZString.decompressFromEncodedURIComponent(encodedFilters);
          if (decompressed) {
            const parsedConfig = JSON.parse(decompressed) as FilterConfig;
            return parsedConfig;
          }
        }
      } catch (error) {
        console.warn('Failed to parse filters from URL:', error);
      }
    }
    return initialFilterConfig;
  });

  const [availableFields, setAvailableFields] = useState<FieldOption[]>(initialAvailableFields);

  // Update URL when filter config changes (if URL filters enabled)
  useEffect(() => {
    if (!enableUrlFilters) return;

    const updateUrl = () => {
      const url = new URL(window.location.href);
      
      if (filterConfig && (filterConfig.enabled && filterConfig.config)) {
        try {
          const compressed = LZString.compressToEncodedURIComponent(JSON.stringify(filterConfig));
          url.searchParams.set(URL_FILTER_PARAM, compressed);
        } catch (error) {
          console.warn('Failed to encode filters to URL:', error);
          url.searchParams.delete(URL_FILTER_PARAM);
        }
      } else {
        url.searchParams.delete(URL_FILTER_PARAM);
      }

      // Update URL without causing a page refresh
      window.history.replaceState({}, '', url.toString());
    };

    updateUrl();
  }, [filterConfig, enableUrlFilters]);

  // Call external onChange callback
  useEffect(() => {
    if (onFilterChange) {
      onFilterChange(filterConfig);
    } else {
    }
  }, [filterConfig, onFilterChange]);

  const setFilterConfig = useCallback((config: FilterConfig | undefined) => {
    setFilterConfigState(config);
  }, []);

  const resetFilters = useCallback(() => {
    const emptyConfig: FilterConfig = {
      enabled: false,
      config: undefined
    };
    setFilterConfig(emptyConfig);
    
    // Also clear the filter panel if it exists
    if (filterRef.current) {
      filterRef.current.clearFilters();
    }
  }, [setFilterConfig]);

  const contextValue = useMemo((): DashboardFilterContextType => ({
    filterConfig,
    setFilterConfig,
    filterRef,
    availableFields,
    setAvailableFields,
    resetFilters,
    enableUrlFilters
  }), [
    filterConfig,
    setFilterConfig,
    availableFields,
    resetFilters,
    enableUrlFilters
  ]);

  return (
    <DashboardFilterContext.Provider value={contextValue}>
      {children}
    </DashboardFilterContext.Provider>
  );
};